// @boty/ai - GPT-4o Vision integration for image analysis

import OpenAI from 'openai';
import type { AIUsageCallback } from './types';

/**
 * Result from image analysis
 */
export interface ImageAnalysisResult {
  /** Text extracted via OCR from the image */
  extractedText: string;
  /** AI-generated description of the image content */
  description: string;
  /** Combined content for bot knowledge */
  combinedContent: string;
}

/**
 * Options for image analysis
 */
export interface ImageAnalysisOptions {
  /** Image detail level: 'low' for fast/cheap, 'high' for better OCR */
  detail?: 'low' | 'high' | 'auto';
  /** Maximum tokens for the response */
  maxTokens?: number;
  /** Optional callback for reporting LLM usage data */
  onUsage?: AIUsageCallback;
}

/**
 * Result from PDF analysis
 */
export interface PdfAnalysisResult {
  /** Extracted content from the PDF */
  content: string;
}

/**
 * Options for PDF analysis
 */
export interface PdfAnalysisOptions {
  /** Maximum tokens for the response */
  maxTokens?: number | undefined;
  /** Original filename for context */
  filename?: string | undefined;
  /** Optional callback for reporting LLM usage data */
  onUsage?: AIUsageCallback;
}

/**
 * Error codes for vision API failures
 */
export type VisionErrorCode =
  | 'OPENAI_API_ERROR'
  | 'RATE_LIMIT_EXCEEDED'
  | 'INVALID_IMAGE'
  | 'INVALID_PDF'
  | 'PARSE_ERROR'
  | 'TIMEOUT';

/**
 * Custom error class for vision API failures
 */
export class VisionError extends Error {
  code: VisionErrorCode;

  constructor(code: VisionErrorCode, message: string) {
    super(message);
    this.name = 'VisionError';
    this.code = code;
  }
}

// Prompt for extracting text and describing image content
const ANALYSIS_PROMPT = `Analyze this image for a bot knowledge base.

Please provide:
1. EXTRACTED TEXT: Any text visible in the image (OCR). If no text is visible, say "No text detected."
2. CONTENT DESCRIPTION: A detailed description of what the image shows, including:
   - Key information visible
   - Data in tables/charts if present
   - Names, dates, locations mentioned
   - Any relevant context that would help answer questions about this content

Format your response EXACTLY as:
EXTRACTED TEXT:
[text here]

CONTENT DESCRIPTION:
[description here]`;

/**
 * Parses the GPT-4o vision response into structured data
 */
function parseVisionResponse(content: string | null): { extractedText: string; description: string } {
  if (!content) {
    return {
      extractedText: 'No text detected.',
      description: 'Unable to analyze image content.',
    };
  }

  // Extract the EXTRACTED TEXT section
  const extractedTextMatch = content.match(/EXTRACTED TEXT:\s*\n([\s\S]*?)(?=\n\nCONTENT DESCRIPTION:|$)/i);
  const extractedText = extractedTextMatch?.[1]?.trim() || 'No text detected.';

  // Extract the CONTENT DESCRIPTION section
  const descriptionMatch = content.match(/CONTENT DESCRIPTION:\s*\n([\s\S]*?)$/i);
  const description = descriptionMatch?.[1]?.trim() || 'No description available.';

  return { extractedText, description };
}

/**
 * Creates an OpenAI client instance
 * Uses OPENAI_API_KEY environment variable
 */
function createOpenAIClient(): OpenAI {
  const apiKey = process.env.OPENAI_API_KEY;

  if (!apiKey) {
    throw new VisionError(
      'OPENAI_API_ERROR',
      'OPENAI_API_KEY environment variable is not set'
    );
  }

  return new OpenAI({ apiKey });
}

/**
 * Delays execution for retry with exponential backoff
 */
async function delay(attempt: number): Promise<void> {
  const baseDelay = 1000; // 1 second
  const maxDelay = 10000; // 10 seconds
  const delayTime = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  return new Promise((resolve) => setTimeout(resolve, delayTime));
}

/**
 * Analyzes an image using GPT-4o vision API
 *
 * @param imageUrl - Public URL of the image to analyze
 * @param options - Analysis options (detail level, max tokens)
 * @returns Extracted text, description, and combined content
 * @throws VisionError on API failures
 *
 * @example
 * ```typescript
 * const result = await analyzeImage('https://storage.example.com/image.jpg');
 * console.log(result.extractedText); // OCR text
 * console.log(result.description);   // AI description
 * console.log(result.combinedContent); // Combined for bot knowledge
 * ```
 */
export async function analyzeImage(
  imageUrl: string,
  options: ImageAnalysisOptions = {}
): Promise<ImageAnalysisResult> {
  const { detail = 'high', maxTokens = 1500, onUsage } = options;
  const maxRetries = 3;

  const openai = createOpenAIClient();

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const startTime = Date.now();
      const response = await openai.chat.completions.create({
        model: 'gpt-4o', // Must use gpt-4o for vision (not gpt-4o-mini)
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text: ANALYSIS_PROMPT,
              },
              {
                type: 'image_url',
                image_url: {
                  url: imageUrl,
                  detail,
                },
              },
            ],
          },
        ],
        max_tokens: maxTokens,
      });

      const messageContent = response.choices[0]?.message?.content ?? null;
      const { extractedText, description } = parseVisionResponse(messageContent);

      // Combine for bot knowledge
      const combinedContent = [
        extractedText !== 'No text detected.' ? `Text from image:\n${extractedText}` : '',
        `Image content:\n${description}`,
      ]
        .filter(Boolean)
        .join('\n\n');

      if (onUsage) {
        try {
          onUsage({
            model: 'gpt-4o',
            provider: 'openai',
            inputTokens: response.usage?.prompt_tokens ?? 0,
            outputTokens: response.usage?.completion_tokens ?? 0,
            latencyMs: Date.now() - startTime,
            functionName: 'analyzeImage',
            inputMessages: [{ role: 'user', content: ANALYSIS_PROMPT }],
            ...(messageContent && { outputText: messageContent.slice(0, 1000) }),
          });
        } catch { /* never break the call */ }
      }

      return {
        extractedText,
        description,
        combinedContent,
      };
    } catch (error) {
      // Handle specific error types
      if (error instanceof OpenAI.APIError) {
        // Rate limit - retry with backoff
        if (error.status === 429) {
          if (attempt < maxRetries - 1) {
            await delay(attempt);
            continue;
          }
          throw new VisionError('RATE_LIMIT_EXCEEDED', 'Rate limit exceeded. Please try again later.');
        }

        // Invalid image URL or format
        if (error.status === 400) {
          throw new VisionError('INVALID_IMAGE', 'Invalid image format or URL.');
        }

        // Timeout
        if (error.code === 'ETIMEDOUT' || error.message?.includes('timeout')) {
          if (attempt < maxRetries - 1) {
            await delay(attempt);
            continue;
          }
          throw new VisionError('TIMEOUT', 'Request timed out. Please try again.');
        }

        throw new VisionError('OPENAI_API_ERROR', error.message || 'OpenAI API error');
      }

      // Unknown error - wrap and rethrow
      if (error instanceof Error) {
        throw new VisionError('OPENAI_API_ERROR', error.message);
      }

      throw new VisionError('OPENAI_API_ERROR', 'Unknown error occurred');
    }
  }

  // Should not reach here, but TypeScript needs this
  throw new VisionError('OPENAI_API_ERROR', 'Max retries exceeded');
}

/**
 * Generates a preview of the combined content (first N characters)
 */
export function generateImagePreview(content: string, maxLength: number = 500): string {
  if (content.length <= maxLength) {
    return content;
  }

  // Try to cut at a word boundary
  const truncated = content.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > maxLength * 0.8) {
    return truncated.substring(0, lastSpace) + '...';
  }

  return truncated + '...';
}

/**
 * Result from document analysis (DOCX, XLSX)
 */
export interface DocumentAnalysisResult {
  /** Extracted content from the document */
  content: string;
}

/**
 * Options for document analysis
 */
export interface DocumentAnalysisOptions {
  /** Maximum tokens for the response */
  maxTokens?: number | undefined;
  /** Original filename for context */
  filename?: string | undefined;
  /** Document type hint */
  documentType?: 'docx' | 'xlsx' | undefined;
  /** Optional callback for reporting LLM usage data */
  onUsage?: AIUsageCallback;
}

// Prompt for extracting content from DOCX documents
const DOCX_EXTRACTION_PROMPT = `Extract all content from this Word document for a knowledge bot.

Please provide:
1. ALL TEXT CONTENT: Extract every piece of text, preserving:
   - Headers and titles
   - Paragraphs and body text
   - Lists (bulleted, numbered)
   - Tables (convert to readable text format)

2. STRUCTURE: Maintain the document's logical structure and hierarchy.

Output as clean, readable text that can be used to answer questions about this document.`;

// Prompt for extracting content from XLSX documents
const XLSX_EXTRACTION_PROMPT = `Extract all content from this Excel spreadsheet for a knowledge bot.

Please provide:
1. ALL DATA: Extract all data from each sheet, preserving:
   - Sheet names as headers
   - Column headers
   - Row data in a readable format
   - Any formulas results (the calculated values)

2. STRUCTURE: For each sheet, present the data in a clear, organized format.

Output as clean, readable text that can be used to answer questions about this spreadsheet data.`;

/**
 * Analyzes a DOCX or XLSX document using GPT-4o
 *
 * Uses OpenAI's file input capability to extract content from
 * Word documents and Excel spreadsheets.
 *
 * @param docBuffer - Buffer containing the document file data
 * @param options - Analysis options (max tokens, filename, document type)
 * @returns Extracted content from the document
 * @throws VisionError on API failures
 */
export async function analyzeDocument(
  docBuffer: Buffer,
  options: DocumentAnalysisOptions = {}
): Promise<DocumentAnalysisResult> {
  const { maxTokens = 4096, filename = 'document', documentType, onUsage } = options;
  const maxRetries = 3;

  const openai = createOpenAIClient();

  // Determine MIME type and prompt based on document type
  const isXlsx = documentType === 'xlsx' || filename.toLowerCase().endsWith('.xlsx');
  const mimeType = isXlsx
    ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  const prompt = isXlsx ? XLSX_EXTRACTION_PROMPT : DOCX_EXTRACTION_PROMPT;

  // Encode document as base64
  const base64Doc = docBuffer.toString('base64');

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Use type assertion for the file content part
      const fileContent = {
        type: 'file',
        file: {
          filename,
          file_data: `data:${mimeType};base64,${base64Doc}`,
        },
      };

      const startTime = Date.now();
      const response = await openai.chat.completions.create({
        model: 'gpt-4o',
        messages: [
          {
            role: 'user',
            content: [
              fileContent as unknown as OpenAI.Chat.Completions.ChatCompletionContentPartImage,
              {
                type: 'text',
                text: prompt,
              },
            ],
          },
        ],
        max_tokens: maxTokens,
      });

      const content = response.choices[0]?.message?.content ?? '';

      if (!content) {
        throw new VisionError('PARSE_ERROR', 'Empty response from document analysis');
      }

      if (onUsage) {
        try {
          onUsage({
            model: 'gpt-4o',
            provider: 'openai',
            inputTokens: response.usage?.prompt_tokens ?? 0,
            outputTokens: response.usage?.completion_tokens ?? 0,
            latencyMs: Date.now() - startTime,
            functionName: 'analyzeDocument',
            inputMessages: [{ role: 'user', content: prompt }],
            ...(content && { outputText: content.slice(0, 1000) }),
          });
        } catch { /* never break the call */ }
      }

      return { content };
    } catch (error) {
      // Handle specific error types
      if (error instanceof OpenAI.APIError) {
        if (error.status === 429) {
          if (attempt < maxRetries - 1) {
            await delay(attempt);
            continue;
          }
          throw new VisionError('RATE_LIMIT_EXCEEDED', 'Rate limit exceeded. Please try again later.');
        }

        if (error.status === 400) {
          throw new VisionError('INVALID_IMAGE', 'Invalid document format or corrupted file.');
        }

        if (error.code === 'ETIMEDOUT' || error.message?.includes('timeout')) {
          if (attempt < maxRetries - 1) {
            await delay(attempt);
            continue;
          }
          throw new VisionError('TIMEOUT', 'Request timed out. Please try again.');
        }

        throw new VisionError('OPENAI_API_ERROR', error.message || 'OpenAI API error');
      }

      if (error instanceof VisionError) {
        throw error;
      }

      if (error instanceof Error) {
        throw new VisionError('OPENAI_API_ERROR', error.message);
      }

      throw new VisionError('OPENAI_API_ERROR', 'Unknown error occurred');
    }
  }

  throw new VisionError('OPENAI_API_ERROR', 'Max retries exceeded');
}

// Prompt for extracting content from PDF documents
const PDF_EXTRACTION_PROMPT = `Extract all content from this PDF for a knowledge bot.

Please provide:
1. ALL TEXT CONTENT: Extract every piece of text, preserving:
   - Headers and titles
   - Paragraphs and body text
   - Lists (bulleted, numbered)
   - Tables (convert to readable text format)

2. VISUAL CONTENT: For any images, charts, diagrams, or infographics:
   - Describe what they show
   - Extract any data or labels visible
   - Note key insights they convey

3. STRUCTURE: If multi-page, organize content by page order.

Output as clean, readable text that can be used to answer questions about this document.`;

/**
 * Analyzes a PDF document using GPT-4o direct PDF input
 *
 * Uses OpenAI's direct PDF support (March 2025+) to extract content
 * from scanned PDFs and image-based documents that text extraction
 * cannot handle.
 *
 * @param pdfBuffer - Buffer containing the PDF file data
 * @param options - Analysis options (max tokens, filename)
 * @returns Extracted content from the PDF
 * @throws VisionError on API failures
 *
 * @example
 * ```typescript
 * const pdfBuffer = await fs.readFile('document.pdf');
 * const result = await analyzePdf(pdfBuffer, { filename: 'document.pdf' });
 * console.log(result.content); // Extracted text content
 * ```
 */
export async function analyzePdf(
  pdfBuffer: Buffer,
  options: PdfAnalysisOptions = {}
): Promise<PdfAnalysisResult> {
  const { maxTokens = 4096, filename = 'document.pdf', onUsage } = options;
  const maxRetries = 3;

  const openai = createOpenAIClient();

  // Encode PDF as base64
  const base64Pdf = pdfBuffer.toString('base64');

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Use type assertion for the file content part since OpenAI SDK types
      // may not yet reflect the direct PDF input support (March 2025+)
      const fileContent = {
        type: 'file',
        file: {
          filename,
          file_data: `data:application/pdf;base64,${base64Pdf}`,
        },
      };

      const startTime = Date.now();
      const response = await openai.chat.completions.create({
        model: 'gpt-4o', // Must use gpt-4o for PDF input
        messages: [
          {
            role: 'user',
            content: [
              fileContent as unknown as OpenAI.Chat.Completions.ChatCompletionContentPartImage,
              {
                type: 'text',
                text: PDF_EXTRACTION_PROMPT,
              },
            ],
          },
        ],
        max_tokens: maxTokens,
      });

      const content = response.choices[0]?.message?.content ?? '';

      if (!content) {
        throw new VisionError('PARSE_ERROR', 'Empty response from PDF analysis');
      }

      if (onUsage) {
        try {
          onUsage({
            model: 'gpt-4o',
            provider: 'openai',
            inputTokens: response.usage?.prompt_tokens ?? 0,
            outputTokens: response.usage?.completion_tokens ?? 0,
            latencyMs: Date.now() - startTime,
            functionName: 'analyzePdf',
            inputMessages: [{ role: 'user', content: PDF_EXTRACTION_PROMPT }],
            ...(content && { outputText: content.slice(0, 1000) }),
          });
        } catch { /* never break the call */ }
      }

      return { content };
    } catch (error) {
      // Handle specific error types
      if (error instanceof OpenAI.APIError) {
        // Rate limit - retry with backoff
        if (error.status === 429) {
          if (attempt < maxRetries - 1) {
            await delay(attempt);
            continue;
          }
          throw new VisionError('RATE_LIMIT_EXCEEDED', 'Rate limit exceeded. Please try again later.');
        }

        // Invalid PDF format
        if (error.status === 400) {
          throw new VisionError('INVALID_PDF', 'Invalid PDF format or corrupted file.');
        }

        // Timeout
        if (error.code === 'ETIMEDOUT' || error.message?.includes('timeout')) {
          if (attempt < maxRetries - 1) {
            await delay(attempt);
            continue;
          }
          throw new VisionError('TIMEOUT', 'Request timed out. Please try again.');
        }

        throw new VisionError('OPENAI_API_ERROR', error.message || 'OpenAI API error');
      }

      // If it's our own VisionError, rethrow it
      if (error instanceof VisionError) {
        throw error;
      }

      // Unknown error - wrap and rethrow
      if (error instanceof Error) {
        throw new VisionError('OPENAI_API_ERROR', error.message);
      }

      throw new VisionError('OPENAI_API_ERROR', 'Unknown error occurred');
    }
  }

  // Should not reach here, but TypeScript needs this
  throw new VisionError('OPENAI_API_ERROR', 'Max retries exceeded');
}
